import React, { useEffect, useState, createElement } from 'react';
import { ChevronLeft, ChevronRight, Phone, Calendar as CalendarIcon, Home, Clock, X, User, Mail, MessageCircle, ArrowRight, CheckCircle2 } from 'lucide-react';
import { BottomNav } from '../components/BottomNav';
interface CalendarPageProps {
  onBack: () => void;
  onClientClick?: (clientId: number) => void;
  onNetworkClick?: () => void;
  onInventoryClick?: () => void;
  onRMIClick?: () => void;
  onProfileClick?: () => void;
}
type ActivityType = 'call' | 'meeting' | 'site-visit';
type ActivityStatus = 'open' | 'closed';
type CallOutcome = 'answered' | 'not-answered';
type MeetingSiteVisitOutcome = 'done' | 'cancelled';
interface Activity {
  id: number;
  type: ActivityType;
  title: string;
  description: string;
  clientName: string;
  clientInitials: string;
  clientId: number;
  clientPhone: string;
  clientEmail: string;
  time: string;
  timestamp: Date;
  status: ActivityStatus;
  outcome?: CallOutcome | MeetingSiteVisitOutcome;
  closingNote?: string;
}
interface DayActivities {
  date: Date;
  dateLabel: string;
  isToday: boolean;
  isPast: boolean;
  isFuture: boolean;
  activities: Activity[];
}
export function CalendarPage({
  onBack,
  onClientClick,
  onNetworkClick,
  onInventoryClick,
  onRMIClick,
  onProfileClick
}: CalendarPageProps) {
  const [activeFilter, setActiveFilter] = useState<ActivityType | 'all'>('all');
  const [currentDateIndex, setCurrentDateIndex] = useState(0);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closingNote, setClosingNote] = useState('');
  const [selectedOutcome, setSelectedOutcome] = useState<string>('');
  const [showScheduleAfterClose, setShowScheduleAfterClose] = useState(false);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Mock activities data from all clients
  const [activities] = useState<Activity[]>([
  // Jan 18 (2 days ago)
  {
    id: 1,
    type: 'call',
    title: 'Phone Call',
    description: 'Discussed property requirements and budget',
    clientName: 'Ahmed Hamdy Mohamed',
    clientInitials: 'AH',
    clientId: 1,
    clientPhone: '+20 123 456 7890',
    clientEmail: 'ahmed.hamdy@example.com',
    time: '4:00 PM',
    timestamp: new Date(2025, 0, 18, 16, 0),
    status: 'closed',
    outcome: 'answered',
    closingNote: 'Client confirmed interest in villa properties'
  }, {
    id: 2,
    type: 'meeting',
    title: 'Initial Meeting',
    description: 'Discussed client needs and preferences',
    clientName: 'Sarah Ahmed',
    clientInitials: 'SA',
    clientId: 2,
    clientPhone: '+20 123 456 7891',
    clientEmail: 'sarah.ahmed@example.com',
    time: '1:00 PM',
    timestamp: new Date(2025, 0, 18, 13, 0),
    status: 'closed',
    outcome: 'done',
    closingNote: 'Reviewed budget and narrowed down preferences'
  },
  // Jan 19 (Yesterday)
  {
    id: 3,
    type: 'call',
    title: 'Follow-up Call',
    description: 'Discuss final property selection',
    clientName: 'Ahmed Hamdy Mohamed',
    clientInitials: 'AH',
    clientId: 1,
    clientPhone: '+20 123 456 7890',
    clientEmail: 'ahmed.hamdy@example.com',
    time: '2:00 PM',
    timestamp: new Date(2025, 0, 19, 14, 0),
    status: 'open'
  }, {
    id: 4,
    type: 'meeting',
    title: 'Property Discussion',
    description: 'Review budget and preferences',
    clientName: 'Sarah Ahmed',
    clientInitials: 'SA',
    clientId: 2,
    clientPhone: '+20 123 456 7891',
    clientEmail: 'sarah.ahmed@example.com',
    time: '10:00 AM',
    timestamp: new Date(2025, 0, 19, 10, 0),
    status: 'open'
  }, {
    id: 5,
    type: 'site-visit',
    title: 'Property Viewing',
    description: 'Visited 3 properties in New Cairo',
    clientName: 'Mohamed Ali',
    clientInitials: 'MA',
    clientId: 3,
    clientPhone: '+20 123 456 7892',
    clientEmail: 'mohamed.ali@example.com',
    time: '11:00 AM',
    timestamp: new Date(2025, 0, 19, 11, 0),
    status: 'open'
  },
  // Jan 20 (Today)
  {
    id: 6,
    type: 'call',
    title: 'Price Negotiation',
    description: 'Discuss offer for Villa A-105',
    clientName: 'Karim Mahmoud',
    clientInitials: 'KM',
    clientId: 4,
    clientPhone: '+20 123 456 7893',
    clientEmail: 'karim.mahmoud@example.com',
    time: '11:00 AM',
    timestamp: new Date(2025, 0, 20, 11, 0),
    status: 'open'
  }, {
    id: 7,
    type: 'site-visit',
    title: 'Property Viewing',
    description: 'Modern Apartment in Waterway',
    clientName: 'Mohamed Ali',
    clientInitials: 'MA',
    clientId: 3,
    clientPhone: '+20 123 456 7892',
    clientEmail: 'mohamed.ali@example.com',
    time: '3:00 PM',
    timestamp: new Date(2025, 0, 20, 15, 0),
    status: 'open'
  }, {
    id: 8,
    type: 'meeting',
    title: 'Contract Review',
    description: 'Sign purchase agreement',
    clientName: 'Nadia Hassan',
    clientInitials: 'NH',
    clientId: 5,
    clientPhone: '+20 123 456 7894',
    clientEmail: 'nadia.hassan@example.com',
    time: '5:00 PM',
    timestamp: new Date(2025, 0, 20, 17, 0),
    status: 'open'
  },
  // Jan 21 (Tomorrow)
  {
    id: 9,
    type: 'site-visit',
    title: 'Site Visit',
    description: 'Luxury Villa with Garden - New Cairo',
    clientName: 'Ahmed Hamdy Mohamed',
    clientInitials: 'AH',
    clientId: 1,
    clientPhone: '+20 123 456 7890',
    clientEmail: 'ahmed.hamdy@example.com',
    time: '3:00 PM',
    timestamp: new Date(2025, 0, 21, 15, 0),
    status: 'open'
  }, {
    id: 10,
    type: 'call',
    title: 'Follow-up Call',
    description: 'Check on financing approval',
    clientName: 'Omar Khaled',
    clientInitials: 'OK',
    clientId: 6,
    clientPhone: '+20 123 456 7895',
    clientEmail: 'omar.khaled@example.com',
    time: '10:00 AM',
    timestamp: new Date(2025, 0, 21, 10, 0),
    status: 'open'
  },
  // Jan 25
  {
    id: 11,
    type: 'meeting',
    title: 'Property Viewing',
    description: 'Modern Apartment in Waterway',
    clientName: 'Sarah Ahmed',
    clientInitials: 'SA',
    clientId: 2,
    clientPhone: '+20 123 456 7891',
    clientEmail: 'sarah.ahmed@example.com',
    time: '11:00 AM',
    timestamp: new Date(2025, 0, 25, 11, 0),
    status: 'open'
  }, {
    id: 12,
    type: 'site-visit',
    title: 'Property Tour',
    description: 'Duplex in Hyde Park',
    clientName: 'Karim Mahmoud',
    clientInitials: 'KM',
    clientId: 4,
    clientPhone: '+20 123 456 7893',
    clientEmail: 'karim.mahmoud@example.com',
    time: '2:00 PM',
    timestamp: new Date(2025, 0, 25, 14, 0),
    status: 'open'
  },
  // Jan 27
  {
    id: 13,
    type: 'call',
    title: 'Check-in Call',
    description: 'Discuss next steps',
    clientName: 'Nadia Hassan',
    clientInitials: 'NH',
    clientId: 5,
    clientPhone: '+20 123 456 7894',
    clientEmail: 'nadia.hassan@example.com',
    time: '4:00 PM',
    timestamp: new Date(2025, 0, 27, 16, 0),
    status: 'open'
  }]);
  const getActivityIcon = (type: ActivityType) => {
    const icons = {
      call: Phone,
      meeting: CalendarIcon,
      'site-visit': Home
    };
    return icons[type];
  };
  const getActivityColor = (type: ActivityType) => {
    const colors = {
      call: 'bg-blue-50 text-blue-500',
      meeting: 'bg-purple-50 text-purple-500',
      'site-visit': 'bg-green-50 text-green-500'
    };
    return colors[type];
  };
  // Group activities by date
  const groupActivitiesByDate = (): DayActivities[] => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Filter activities by type
    const filteredActivities = activities.filter(activity => activeFilter === 'all' || activity.type === activeFilter);
    // Group by date
    const grouped = new Map<string, Activity[]>();
    filteredActivities.forEach(activity => {
      const activityDate = new Date(activity.timestamp);
      activityDate.setHours(0, 0, 0, 0);
      const dateKey = activityDate.toISOString();
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey)!.push(activity);
    });
    // Convert to array and sort by date
    const dayActivities: DayActivities[] = Array.from(grouped.entries()).map(([dateKey, acts]) => {
      const date = new Date(dateKey);
      const isToday = date.getTime() === today.getTime();
      const isPast = date < today;
      const isFuture = date > today;
      // Sort activities by time
      acts.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      // Format date label
      let dateLabel = '';
      const diffDays = Math.floor((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      if (isToday) {
        dateLabel = 'Today';
      } else if (diffDays === -1) {
        dateLabel = 'Yesterday';
      } else if (diffDays === 1) {
        dateLabel = 'Tomorrow';
      } else {
        dateLabel = date.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric'
        });
      }
      return {
        date,
        dateLabel,
        isToday,
        isPast,
        isFuture,
        activities: acts
      };
    }).sort((a, b) => a.date.getTime() - b.date.getTime());
    return dayActivities;
  };
  const dayActivities = groupActivitiesByDate();
  // Find today's index
  useEffect(() => {
    const todayIndex = dayActivities.findIndex(day => day.isToday);
    if (todayIndex !== -1) {
      setCurrentDateIndex(todayIndex);
    }
  }, [activeFilter]);
  const currentDay = dayActivities[currentDateIndex];
  const canGoPrevious = currentDateIndex > 0;
  const canGoNext = currentDateIndex < dayActivities.length - 1;
  const goToPreviousDay = () => {
    if (canGoPrevious) {
      setCurrentDateIndex(currentDateIndex - 1);
    }
  };
  const goToNextDay = () => {
    if (canGoNext) {
      setCurrentDateIndex(currentDateIndex + 1);
    }
  };
  const goToToday = () => {
    const todayIndex = dayActivities.findIndex(day => day.isToday);
    if (todayIndex !== -1) {
      setCurrentDateIndex(todayIndex);
    }
  };
  const handleCloseActivity = () => {
    setShowCloseModal(true);
  };
  const handleConfirmClose = () => {
    if (!selectedActivity || !selectedOutcome) return;
    // Update activity status
    const updatedActivities = activities.map(act => act.id === selectedActivity.id ? {
      ...act,
      status: 'closed' as ActivityStatus,
      outcome: selectedOutcome as CallOutcome | MeetingSiteVisitOutcome,
      closingNote: closingNote
    } : act);
    // In a real app, you would update the state here
    // For now, we'll just close the modals
    setShowCloseModal(false);
    setShowScheduleAfterClose(true);
  };
  const handleScheduleNewActivity = () => {
    // Close all modals and reset
    setShowScheduleAfterClose(false);
    setSelectedActivity(null);
    setClosingNote('');
    setSelectedOutcome('');
    // In a real app, this would open the schedule activity modal
    alert('Schedule new activity modal would open here');
  };
  const handleSkipSchedule = () => {
    setShowScheduleAfterClose(false);
    setSelectedActivity(null);
    setClosingNote('');
    setSelectedOutcome('');
  };
  const getOutcomeOptions = (type: ActivityType) => {
    if (type === 'call') {
      return [{
        value: 'answered',
        label: 'Answered',
        color: 'bg-green-50 text-green-600 border-green-200'
      }, {
        value: 'not-answered',
        label: 'Not Answered',
        color: 'bg-red-50 text-red-600 border-red-200'
      }];
    }
    return [{
      value: 'done',
      label: 'Done',
      color: 'bg-green-50 text-green-600 border-green-200'
    }, {
      value: 'cancelled',
      label: 'Cancelled',
      color: 'bg-red-50 text-red-600 border-red-200'
    }];
  };
  const getOutcomeLabel = (activity: Activity) => {
    if (!activity.outcome) return '';
    const outcomeLabels: Record<string, string> = {
      answered: 'Answered',
      'not-answered': 'Not Answered',
      done: 'Done',
      cancelled: 'Cancelled'
    };
    return outcomeLabels[activity.outcome] || '';
  };
  const getOutcomeColor = (outcome: string) => {
    if (outcome === 'answered' || outcome === 'done') {
      return 'bg-green-100 text-green-700';
    }
    return 'bg-red-100 text-red-700';
  };
  const renderActivityCard = (activity: Activity) => {
    const Icon = getActivityIcon(activity.type);
    const colorClass = getActivityColor(activity.type);
    const outcomeOptions = getOutcomeOptions(activity.type);
    return <div key={activity.id} className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden" data-id="element-269">
        {/* Activity Info - Clickable */}
        <button onClick={() => setSelectedActivity(activity)} className="w-full p-4 text-left hover:bg-gray-50 transition-colors" data-id="element-270">
          <div className="flex gap-3" data-id="element-271">
            {/* Activity Icon */}
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`} data-id="element-272">
              <Icon className="w-5 h-5" data-id="element-273" />
            </div>

            {/* Activity Info */}
            <div className="flex-1 min-w-0" data-id="element-274">
              <div className="flex items-start justify-between mb-1" data-id="element-275">
                <div className="flex items-center gap-2" data-id="element-276">
                  <h3 className="font-semibold text-neutral-text text-sm" data-id="element-277">
                    {activity.title}
                  </h3>
                  {activity.status === 'closed' && activity.outcome && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${getOutcomeColor(activity.outcome)}`} data-id="element-278">
                      {getOutcomeLabel(activity)}
                    </span>}
                </div>
                <span className="text-xs font-medium text-gray-500 ml-2 flex-shrink-0" data-id="element-279">
                  {activity.time}
                </span>
              </div>

              <p className="text-sm text-gray-600 mb-2" data-id="element-280">
                {activity.description}
              </p>

              {activity.status === 'closed' && activity.closingNote && <p className="text-xs text-gray-500 italic mb-2 bg-gray-50 p-2 rounded" data-id="element-281">
                  Note: {activity.closingNote}
                </p>}

              <div className="flex items-center gap-2" data-id="element-282">
                <div className="w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center" data-id="element-283">
                  <span className="text-[10px] font-semibold text-secondary" data-id="element-284">
                    {activity.clientInitials}
                  </span>
                </div>
                <span className="text-xs text-gray-500" data-id="element-285">
                  {activity.clientName}
                </span>
              </div>
            </div>
          </div>
        </button>

        {/* Quick Action Buttons - Only for Open Activities */}
        {activity.status === 'open' && <div className="px-4 pb-4 pt-2 border-t border-gray-100" data-id="element-286">
            <div className="grid grid-cols-2 gap-2" data-id="element-287">
              {outcomeOptions.map(option => <button key={option.value} onClick={e => {
            e.stopPropagation();
            setSelectedActivity(activity);
            setSelectedOutcome(option.value);
            setShowCloseModal(true);
          }} className={`py-2 px-3 rounded-lg font-semibold text-xs transition-all border ${option.color} border-current hover:opacity-80`} data-id="element-288">
                  {option.label}
                </button>)}
            </div>
          </div>}
      </div>;
  };
  return <div className="min-h-screen bg-neutral-bg pb-20 font-sans w-full" data-id="element-289">
      <div className="w-full mx-auto bg-neutral-bg min-h-screen relative" data-id="element-290">
        {/* Header */}
        <div className="bg-primary px-4 py-4 safe-top flex items-center justify-between sticky top-0 z-10 shadow-sm" data-id="element-291">
          <button onClick={onBack} className="p-2 -ml-2 text-white hover:bg-white/10 rounded-full transition-colors" data-id="element-292">
            <ChevronLeft className="w-6 h-6" data-id="element-293" />
          </button>
          <h1 className="font-semibold text-lg text-white" data-id="element-294">Calendar</h1>
          <div className="w-10" data-id="element-295"></div>
        </div>

        {/* Filter Pills */}
        <div className="px-4 pt-4 pb-3 bg-neutral-bg sticky top-[72px] z-10" data-id="element-296">
          <div className="flex gap-2 overflow-x-auto no-scrollbar" data-id="element-297">
            <button onClick={() => setActiveFilter('all')} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${activeFilter === 'all' ? 'bg-secondary text-white' : 'bg-white text-gray-600 border border-gray-200'}`} data-id="element-298">
              All
            </button>
            <button onClick={() => setActiveFilter('call')} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${activeFilter === 'call' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 border border-gray-200'}`} data-id="element-299">
              Calls
            </button>
            <button onClick={() => setActiveFilter('meeting')} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${activeFilter === 'meeting' ? 'bg-purple-500 text-white' : 'bg-white text-gray-600 border border-gray-200'}`} data-id="element-300">
              Meetings
            </button>
            <button onClick={() => setActiveFilter('site-visit')} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${activeFilter === 'site-visit' ? 'bg-green-500 text-white' : 'bg-white text-gray-600 border border-gray-200'}`} data-id="element-301">
              Site Visits
            </button>
          </div>
        </div>

        {/* Date Navigation */}
        <div className="px-4 pb-4" data-id="element-302">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-id="element-303">
            <div className="flex items-center justify-between mb-4" data-id="element-304">
              <button onClick={goToPreviousDay} disabled={!canGoPrevious} className={`p-2 rounded-full transition-colors ${canGoPrevious ? 'text-secondary hover:bg-secondary/10' : 'text-gray-300 cursor-not-allowed'}`} data-id="element-305">
                <ChevronLeft className="w-6 h-6" data-id="element-306" />
              </button>

              <div className="flex-1 text-center" data-id="element-307">
                <h2 className="text-xl font-bold text-neutral-text" data-id="element-308">
                  {currentDay?.dateLabel}
                </h2>
                <p className="text-sm text-gray-500" data-id="element-309">
                  {currentDay?.date.toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
                </p>
              </div>

              <button onClick={goToNextDay} disabled={!canGoNext} className={`p-2 rounded-full transition-colors ${canGoNext ? 'text-secondary hover:bg-secondary/10' : 'text-gray-300 cursor-not-allowed'}`} data-id="element-310">
                <ChevronRight className="w-6 h-6" data-id="element-311" />
              </button>
            </div>

            {!currentDay?.isToday && <button onClick={goToToday} className="w-full bg-secondary/10 hover:bg-secondary/20 text-secondary font-semibold py-2 rounded-lg transition-colors text-sm" data-id="element-312">
                Go to Today
              </button>}
          </div>
        </div>

        {/* Activities for Current Date */}
        <div className="px-4 pb-4" data-id="element-313">
          {currentDay && currentDay.activities.length > 0 ? <>
              <div className="flex items-center justify-between mb-3" data-id="element-314">
                <h3 className="text-sm font-bold text-neutral-text uppercase tracking-wide" data-id="element-315">
                  Activities
                </h3>
                <span className="bg-secondary/20 text-secondary text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-316">
                  {currentDay.activities.length}
                </span>
              </div>
              <div className="space-y-3" data-id="element-317">
                {currentDay.activities.map(activity => renderActivityCard(activity))}
              </div>
            </> : <div className="bg-white rounded-xl p-8 text-center shadow-sm border border-gray-100" data-id="element-318">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-319">
                <CalendarIcon className="w-8 h-8 text-gray-400" data-id="element-320" />
              </div>
              <h3 className="text-lg font-semibold text-neutral-text mb-2" data-id="element-321">
                No Activities
              </h3>
              <p className="text-sm text-gray-500" data-id="element-322">
                No activities scheduled for this date
              </p>
            </div>}
        </div>

        <BottomNav onNetworkClick={onNetworkClick} onInventoryClick={onInventoryClick} onRMIClick={onRMIClick} onProfileClick={onProfileClick} data-id="element-323" />

        {/* Client Details Drawer */}
        {selectedActivity && <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center" data-id="element-324">
            <div className="bg-white w-full max-w-md rounded-t-3xl pb-8 animate-slide-up" data-id="element-325">
              <div className="px-6 pt-8 pb-6" data-id="element-326">
                {/* Close Button */}
                <button onClick={() => setSelectedActivity(null)} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-neutral-bg rounded-full transition-colors" data-id="element-327">
                  <X className="w-5 h-5" data-id="element-328" />
                </button>

                {/* Activity Info */}
                <div className="mb-6" data-id="element-329">
                  <div className="flex items-center gap-3 mb-3" data-id="element-330">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getActivityColor(selectedActivity.type)}`} data-id="element-331">
                      {createElement(getActivityIcon(selectedActivity.type), {
                    className: 'w-6 h-6'
                  })}
                    </div>
                    <div data-id="element-332">
                      <h3 className="font-bold text-neutral-text text-lg" data-id="element-333">
                        {selectedActivity.title}
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-334">
                        {selectedActivity.time}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 ml-15" data-id="element-335">
                    {selectedActivity.description}
                  </p>
                </div>

                {/* Client Info */}
                <div className="bg-neutral-bg rounded-xl p-4 mb-6" data-id="element-336">
                  <div className="flex items-center gap-3 mb-4" data-id="element-337">
                    <div className="w-14 h-14 rounded-full bg-secondary/20 flex items-center justify-center" data-id="element-338">
                      <span className="text-lg font-bold text-secondary" data-id="element-339">
                        {selectedActivity.clientInitials}
                      </span>
                    </div>
                    <div className="flex-1" data-id="element-340">
                      <h4 className="font-bold text-neutral-text" data-id="element-341">
                        {selectedActivity.clientName}
                      </h4>
                      <p className="text-xs text-gray-500" data-id="element-342">Client</p>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-3" data-id="element-343">
                    <div className="flex items-center gap-3 text-sm" data-id="element-344">
                      <Phone className="w-4 h-4 text-gray-400" data-id="element-345" />
                      <span className="text-gray-600" data-id="element-346">
                        {selectedActivity.clientPhone}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm" data-id="element-347">
                      <Mail className="w-4 h-4 text-gray-400" data-id="element-348" />
                      <span className="text-gray-600" data-id="element-349">
                        {selectedActivity.clientEmail}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3" data-id="element-350">
                  {selectedActivity.status === 'open' && <button onClick={handleCloseActivity} className="w-full bg-accent hover:bg-accent-dark text-primary font-semibold py-3 rounded-lg transition-colors" data-id="element-351">
                      Close Activity
                    </button>}

                  <button onClick={() => {
                if (onClientClick) {
                  onClientClick(selectedActivity.clientId);
                }
                setSelectedActivity(null);
              }} className="w-full bg-secondary hover:bg-secondary-dark text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2" data-id="element-352">
                    <User className="w-5 h-5" data-id="element-353" />
                    View Client Profile
                    <ArrowRight className="w-4 h-4" data-id="element-354" />
                  </button>

                  <div className="grid grid-cols-2 gap-3" data-id="element-355">
                    <button className="flex items-center justify-center gap-2 py-3 px-4 bg-green-50 hover:bg-green-100 text-green-600 rounded-lg transition-colors font-medium text-sm" data-id="element-356">
                      <MessageCircle className="w-4 h-4" data-id="element-357" />
                      WhatsApp
                    </button>
                    <button className="flex items-center justify-center gap-2 py-3 px-4 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors font-medium text-sm" data-id="element-358">
                      <Phone className="w-4 h-4" data-id="element-359" />
                      Call
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>}

        {/* Close Activity Modal */}
        {showCloseModal && selectedActivity && selectedOutcome && <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center" data-id="element-360">
            <div className="bg-white w-full max-w-md rounded-t-3xl pb-8 animate-slide-up" data-id="element-361">
              <div className="px-6 pt-8 pb-6" data-id="element-362">
                {/* Close Button */}
                <button onClick={() => {
              setShowCloseModal(false);
              setClosingNote('');
              setSelectedOutcome('');
            }} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-neutral-bg rounded-full transition-colors" data-id="element-363">
                  <X className="w-5 h-5" data-id="element-364" />
                </button>

                <h2 className="text-xl font-bold text-neutral-text mb-6" data-id="element-365">
                  Close Activity as{' '}
                  <span className={`${selectedOutcome === 'answered' || selectedOutcome === 'done' ? 'text-green-600' : 'text-red-600'}`} data-id="element-366">
                    {getOutcomeOptions(selectedActivity.type).find(o => o.value === selectedOutcome)?.label}
                  </span>
                </h2>

                {/* Activity Summary */}
                <div className="bg-neutral-bg rounded-lg p-4 mb-6" data-id="element-367">
                  <div className="flex items-center gap-3" data-id="element-368">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getActivityColor(selectedActivity.type)}`} data-id="element-369">
                      {createElement(getActivityIcon(selectedActivity.type), {
                    className: 'w-5 h-5'
                  })}
                    </div>
                    <div data-id="element-370">
                      <h3 className="font-semibold text-neutral-text" data-id="element-371">
                        {selectedActivity.title}
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-372">
                        {selectedActivity.clientName} • {selectedActivity.time}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Note */}
                <div className="mb-6" data-id="element-373">
                  <label className="block text-sm font-medium text-neutral-text mb-2" data-id="element-374">
                    Add Note (Optional)
                  </label>
                  <textarea value={closingNote} onChange={e => setClosingNote(e.target.value)} placeholder="Add any notes about this activity..." rows={4} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent resize-none text-sm" data-id="element-375" />
                </div>

                {/* Confirm Button */}
                <button onClick={handleConfirmClose} className="w-full bg-secondary hover:bg-secondary-dark text-white font-semibold py-3 rounded-lg transition-colors" data-id="element-376">
                  Confirm & Close
                </button>
              </div>
            </div>
          </div>}

        {/* Schedule After Close Modal */}
        {showScheduleAfterClose && selectedActivity && <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-id="element-377">
            <div className="bg-white w-full max-w-sm rounded-2xl shadow-xl" data-id="element-378">
              <div className="p-6" data-id="element-379">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" data-id="element-380">
                  <CheckCircle2 className="w-6 h-6 text-green-600" data-id="element-381" />
                </div>
                <h3 className="text-lg font-bold text-neutral-text text-center mb-2" data-id="element-382">
                  Activity Closed!
                </h3>
                <p className="text-sm text-gray-600 text-center mb-6" data-id="element-383">
                  Would you like to schedule a follow-up activity with{' '}
                  {selectedActivity.clientName}?
                </p>
                <div className="space-y-3" data-id="element-384">
                  <button onClick={handleScheduleNewActivity} className="w-full bg-secondary hover:bg-secondary-dark text-white font-semibold py-3 rounded-lg transition-colors" data-id="element-385">
                    Schedule New Activity
                  </button>
                  <button onClick={handleSkipSchedule} className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-lg transition-colors" data-id="element-386">
                    Skip for Now
                  </button>
                </div>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}